import { EventClickArg } from '@fullcalendar/core';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import ListIcon from '@mui/icons-material/List';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, Portal, SelectChangeEvent, Snackbar, Stack } from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Title } from 'react-admin';
import { useHistory, useLocation } from 'react-router-dom';
import { ClaimStatus, ClaimType, EhrClaim } from '../../interfaces/billing';
import { IClinic } from '../../interfaces/clinic';
import { SelectOption } from '../../interfaces/common';
import { getClinicsForSelect } from '../../lib/clinic';
import { BillingService } from '../../services';
import { StyledTheme } from '../../styles/styleTheme';
import { PaginateAbortError } from '../../utils/paginateAll';
import { paginateAllBatched } from '../../utils/paginateAllBatched';
import { AppDrawer } from '../common/AppDrawer/AppDrawer';
import { CustomTabs } from '../common/CustomTabs/CustomTabs';
import { Loader } from '../common/Loader';
import { TabPanel } from '../common/TabPanel/TabPanel';
import { ClaimDetails } from './ClaimDetails';
import { ClaimLegend } from './ClaimLegend';
import './claims.css';
import { ClaimsCalendar } from './ClaimsCalendar';
import { ClaimsList } from './ClaimsList';
import { ClaimSyncRequests } from './ClaimSyncRequests';
import { Filters } from './Filters';
import { SyncClaimsDrawer } from './SyncClaimsDrawer';

type ClaimsView = 'calendar' | 'list';
const VIEW_TABS: ClaimsView[] = ['calendar', 'list'];

export const Claims: React.FC = () => {
  const [clinicOptions, setClinicOptions] = useState<SelectOption[]>([]);
  const [clinics, setClinics] = useState<string[]>([]);

  // For sync
  const [fromSyncDate, setFromSyncDate] = useState(
    moment().subtract(7, 'days').format('YYYY-MM-DD'),
  );
  const [untilSyncDate, setUntilSyncDate] = useState(moment().format('YYYY-MM-DD'));

  // For calendar and filters
  const [fromFilterDate, setFromFilterDate] = useState<string | null>(
    moment().subtract(7, 'days').format('YYYY-MM-DD'),
  );
  const [untilFilterDate, setUntilFilterDate] = useState<string | null>(
    moment().format('YYYY-MM-DD'),
  );

  const [forceEhrFetching, setForceEhrFetching] = useState(false);
  const [forceResyncEhrFetching, setForceResyncEhrFetching] = useState(false);
  const [status, setStatus] = useState<string[]>([
    ClaimStatus.Pending,
    ClaimStatus.Uploaded,
    ClaimStatus.UploadedWithErrors,
    ClaimStatus.Processed,
  ]);
  const [claimType, setClaimType] = useState<string[]>([
    ClaimType.Appointment,
    ClaimType.RPM,
    ClaimType.Unknown,
  ]);
  const [selectedClaim, setSelectedClaim] = useState<EhrClaim | null>(null);
  const [selectedClaimLoading, setSelectedClaimLoading] = useState(false);
  const [viewingClaimRequests, setViewingClaimRequests] = useState(false);
  const [claims, setClaims] = useState<EhrClaim[]>([]);
  const [message, setMessage] = useState<string>('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [pageProgress, setPageProgress] = useState<{ page: number; totalPages: number } | null>(
    null,
  );
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const fetchAbortRef = useRef<AbortController | null>(null);

  const history = useHistory();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const view = useMemo<ClaimsView>(() => {
    const requested = searchParams.get('view');
    return requested === 'list' ? 'list' : 'calendar';
  }, [searchParams]);
  const viewIndex = VIEW_TABS.indexOf(view);

  const patient = useMemo<string[]>(() => {
    const raw = searchParams.get('patientIds');
    return raw ? raw.split(',').filter(Boolean) : [];
  }, [searchParams]);

  const claimIdInUrl = useMemo<number | null>(() => {
    const raw = searchParams.get('claimId');
    if (!raw) return null;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }, [searchParams]);

  const updateSearch = (mutator: (params: URLSearchParams) => void, replace = true) => {
    const next = new URLSearchParams(location.search);
    mutator(next);
    const qs = next.toString();
    const target = { pathname: location.pathname, search: qs ? `?${qs}` : '' };
    if (replace) {
      history.replace(target);
    } else {
      history.push(target);
    }
  };

  const handleViewChange = (_: React.SyntheticEvent, newIndex: number) => {
    const next = VIEW_TABS[newIndex] ?? 'calendar';
    updateSearch((params) => {
      if (next === 'calendar') {
        params.delete('view');
      } else {
        params.set('view', next);
      }
    });
  };

  const openClaimInUrl = (claimId: number) => {
    updateSearch((params) => params.set('claimId', `${claimId}`), false);
  };

  const closeClaimInUrl = () => {
    updateSearch((params) => params.delete('claimId'));
  };

  useEffect(() => {
    fetchClinics();
  }, []);

  const getRevenuePrefix = (revenueCode?: string | null) => {
    if (!revenueCode) {
      return '';
    }
    const lastDashIndex = revenueCode.lastIndexOf('-');
    return lastDashIndex > 0 ? revenueCode.substring(0, lastDashIndex) : revenueCode;
  };

  const fetchClinics = async () => {
    const clinics = await getClinicsForSelect();
    if (!clinics.length) {
      return;
    }

    const clinicGroups: { [groupKey: string]: IClinic[] } = {};

    for (const clinic of clinics.sort((a, b) => a.id - b.id)) {
      if (!!clinic.ehr_config?.drchrono?.client_id) {
        const clientId = clinic.ehr_config.drchrono.client_id;
        const revenueCode = clinic.ehr_config.drchrono.revenue_account_code;
        const revenuePrefix = getRevenuePrefix(revenueCode);

        const clientGroupKey = `client_${clientId}`;
        clinicGroups[clientGroupKey] = clinicGroups[clientGroupKey] || [];
        clinicGroups[clientGroupKey].push(clinic);

        if (revenuePrefix) {
          const revenueGroupKey = `revenue_${revenuePrefix}`;
          clinicGroups[revenueGroupKey] = clinicGroups[revenueGroupKey] || [];
          clinicGroups[revenueGroupKey].push(clinic);
        }
      }
    }

    const clinicToGroupKey: { [clinicId: number]: string } = {};

    for (const clinic of clinics) {
      if (!clinic.ehr_config?.drchrono?.client_id) {
        continue;
      }

      const clientId = clinic.ehr_config.drchrono.client_id;
      const revenueCode = clinic.ehr_config.drchrono.revenue_account_code;
      const revenuePrefix = getRevenuePrefix(revenueCode);

      const clientGroupKey = `client_${clientId}`;
      const revenueGroupKey = `revenue_${revenuePrefix}`;

      const clientGroupSize = clinicGroups[clientGroupKey]?.length || 0;
      const revenueGroupSize = revenuePrefix ? clinicGroups[revenueGroupKey]?.length || 0 : 0;

      clinicToGroupKey[clinic.id] =
        revenueGroupSize > clientGroupSize ? revenueGroupKey : clientGroupKey;
    }

    const filteredClinics: IClinic[] = [];
    for (const clinic of clinics) {
      if (!(clinic.apero_config?.apero_claims_sync_status === 'enabled')) {
        continue;
      }

      if (!clinic.ehr_config?.drchrono?.client_id) {
        continue;
      }

      const groupKey = clinicToGroupKey[clinic.id];
      const clinicGroup = clinicGroups[groupKey];

      if (!clinicGroup || clinicGroup[0].id !== clinic.id) {
        continue;
      }

      // Clinic is the main member of the group

      const groupMembers = clinicGroup.slice(1).map((c) => c.id);
      const groupName = groupMembers.length > 0 ? ` (+ ${groupMembers.join(', ')})` : '';

      filteredClinics.push({
        ...clinic,
        name: `${clinic.name}${groupName}`,
      });
    }

    const clinicOptions = filteredClinics.map((clinic) => ({
      value: `${clinic.id}`,
      label: clinic.name,
    }));
    setClinicOptions(clinicOptions);
    return filteredClinics;
  };

  const handleFetchClaims = async (startDate: string, endDate: string) => {
    let defaultClinicIds: string[] = [];
    if (clinics.length === 0 && clinicOptions.length > 0) {
      defaultClinicIds = [clinicOptions[0].value];
      setClinics(defaultClinicIds);
    } else {
      defaultClinicIds = clinics.map((id) => id);
    }

    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setCalendarLoading(true);
    setPageProgress(null);
    try {
      const service = new BillingService();
      const baseParams = {
        fromDate: startDate,
        untilDate: endDate,
        clinicIds: defaultClinicIds.join(','),
        ...(status.length && { statuses: status.map((s) => s).join(',') }),
        ...(claimType.length && { sources: claimType.map((type) => type).join(',') }),
      };

      const allClaims = await paginateAllBatched<EhrClaim>(
        async (page) => {
          const response = await service.getClaims({ ...baseParams, page, perPage: 100 });
          if (!response) {
            return null;
          }
          if (!response.success) {
            throw new Error(response.message || 'Error fetching claims');
          }
          return { data: response.claims, pagination: response.pagination };
        },
        {
          signal: controller.signal,
          onBatch: (claimsSoFar, page, totalPages) => {
            // Render incrementally as each batch resolves; ignore late ticks
            // from a fetch the user has already navigated away from.
            if (controller.signal.aborted) {
              return;
            }
            setClaims([...claimsSoFar]);
            setPageProgress({ page, totalPages });
          },
        },
      );

      if (controller.signal.aborted) {
        return;
      }
      setClaims(allClaims);
    } catch (error) {
      if (error instanceof PaginateAbortError) {
        return;
      }
      setMessage(`Error fetching claims from range: ${startDate} to ${endDate}: ${error}`);
    } finally {
      if (!controller.signal.aborted) {
        setCalendarLoading(false);
        setPageProgress(null);
      }
    }
  };

  const handleCancelFetch = () => {
    fetchAbortRef.current?.abort();
    setCalendarLoading(false);
    setPageProgress(null);
  };

  const handleClinicChange = (event: SelectChangeEvent<string[]>) => {
    setClinics(event.target.value as string[]);
  };

  const handleFromSyncDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromSyncDate(event.target.value);
  };

  const handleUntilSyncDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUntilSyncDate(event.target.value);
  };

  const handlePatientChange = (value: SelectOption[]) => {
    updateSearch((params) => {
      const ids = value.map((p) => p.value);
      if (ids.length === 0) {
        params.delete('patientIds');
      } else {
        params.set('patientIds', ids.join(','));
      }
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<string[]>) => {
    setStatus(event.target.value as string[]);
  };

  const handleClaimTypeChange = (event: SelectChangeEvent<string[]>) => {
    setClaimType(event.target.value as string[]);
  };

  const handleForceEhrFetch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForceEhrFetching(event.target.checked);
  };

  const handleForceResyncEhrFetch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForceResyncEhrFetching(event.target.checked);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    const claimId = parseInt(clickInfo.event.id);
    if (Number.isFinite(claimId)) {
      openClaimInUrl(claimId);
    }
  };

  const handleSyncRange = async () => {
    try {
      setSyncLoading(true);

      // Validate clinic
      if (clinics.length === 0) {
        setMessage('Please select at least one clinic');
        setSyncLoading(false);
        return;
      }

      const response = await new BillingService().runEhrClaimSync({
        clinicIds: clinics.map((id) => parseInt(id)),
        enableSlackMessages: false,
        forceEhrFetching,
        fromDate: fromSyncDate,
        untilDate: untilSyncDate,
      });

      if (!response || !response.success) {
        setMessage(
          response?.message ||
            `Error syncing claims from range: ${fromSyncDate} to ${untilSyncDate}`,
        );
        return;
      }

      const compactedResults = response.clinics
        .map((clinic) => {
          return clinic.success ? `#${clinic.requestId}` : '';
        })
        .filter((r) => !!r)
        .join(',');

      setMessage(
        `Claims sync requested for range:${fromSyncDate} to ${untilSyncDate}. Request ids:(${compactedResults})`,
      );

      setLastSyncedAt(new Date());
      setSyncDrawerOpen(false);

      if (fromFilterDate && untilFilterDate) {
        await handleFetchClaims(fromFilterDate, untilFilterDate);
      }
    } catch (error) {
      setMessage(`Error syncing claims from range: ${fromSyncDate} to ${untilSyncDate}: ${error}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Fetch the full claim whenever the URL `claimId` changes.
  useEffect(() => {
    if (claimIdInUrl == null) {
      setSelectedClaim(null);
      return;
    }

    let cancelled = false;
    setSelectedClaimLoading(true);
    new BillingService()
      .getClaim(claimIdInUrl)
      .then((response) => {
        if (cancelled) return;
        if (!response) {
          setMessage(`Error fetching claim details for claim ID: ${claimIdInUrl}`);
          setSelectedClaim(null);
          return;
        }
        setSelectedClaim({
          id: claimIdInUrl,
          ...response.claim,
        } as unknown as EhrClaim);
      })
      .catch((error) => {
        if (cancelled) return;
        setMessage(`Error fetching claim details for claim ID: ${claimIdInUrl}: ${error}`);
      })
      .finally(() => {
        if (!cancelled) setSelectedClaimLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [claimIdInUrl]);

  const handleAttemptResync = async () => {
    if (!selectedClaim) {
      setMessage('No claim selected for resync');
      return;
    }

    const response = await new BillingService().reSyncEhrClaim(selectedClaim.id, {
      forceEhrFetching: forceResyncEhrFetching,
    });

    if (!response) {
      setMessage('Unexpected error resyncing claim');
      return;
    }

    if (response.success) {
      setMessage('Claim resync finished. See claim logs for further details.');
    } else if (!!response.message) {
      setMessage(response.message);
    }

    const claimsCopy = [...claims];
    const claimIndex = claimsCopy.findIndex((c) => c.id === response?.claim?.id);
    if (claimIndex < 0) {
      return;
    }

    const newClaim = response.claim!;
    claimsCopy[claimIndex] = newClaim;
    setClaims(claimsCopy);
    setSelectedClaim(newClaim);
  };

  const handleFilter = async () => {
    try {
      setFilterLoading(true);

      // Validate clinic
      if (clinics.length === 0) {
        setMessage('Please select at least one clinic');
        return;
      }

      // Validate ranges
      if (!fromFilterDate || !untilFilterDate) {
        setMessage('Please select a range of dates');
        return;
      }

      await handleFetchClaims(fromFilterDate, untilFilterDate);
    } catch (error) {
      setMessage(
        `Error filtering claims from range: ${fromFilterDate} to ${untilFilterDate}: ${error}`,
      );
    } finally {
      setFilterLoading(false);
    }
  };

  const handleCloseDetails = () => {
    closeClaimInUrl();
  };

  const handleClaimUpdate = (updatedClaim: EhrClaim) => {
    const claimsCopy = [...claims];
    const claimIndex = claimsCopy.findIndex((c) => c.id === updatedClaim.id);
    if (claimIndex >= 0) {
      claimsCopy[claimIndex] = updatedClaim;
      setClaims(claimsCopy);
    }
    setSelectedClaim(updatedClaim);
  };

  const handleDisplaySyncRequests = async () => {
    setViewingClaimRequests(true);
  };

  const handleCloseClaimRequests = async () => {
    setViewingClaimRequests(false);
  };

  // Patient options derived from currently-loaded calendar claims.
  const patientOptionsFromClaims = useMemo<SelectOption[]>(() => {
    const seen = new Map<string, string>();
    for (const claim of claims) {
      const id = claim.corePatient?.id;
      if (id == null) continue;
      const key = `${id}`;
      if (!seen.has(key)) {
        const label = claim.corePatient?.firstName
          ? `${claim.corePatient.firstName} ${claim.corePatient.lastName ?? ''}`.trim()
          : `#${id}`;
        seen.set(key, label);
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [claims]);

  // Patient filter is applied client-side so it survives calendar navigation
  // even when the underlying claim set is replaced.
  const calendarFilteredClaims = useMemo(() => {
    if (patient.length === 0) return claims;
    const allowed = new Set(patient);
    return claims.filter(
      (c) => c.corePatient?.id != null && allowed.has(`${c.corePatient.id}`),
    );
  }, [claims, patient]);

  const handleDateSetClick = async (startDate: string, endDate: string) => {
    setFromFilterDate(startDate.slice(0, 10));
    setUntilFilterDate(endDate.slice(0, 10));

    await handleFetchClaims(
      moment.utc(startDate).format('YYYY-MM-DD'),
      moment.utc(endDate).format('YYYY-MM-DD'),
    );
  };

  if (clinicOptions.length === 0) {
    return <Loader />;
  }

  return (
    <>
      <Title title='Claims' />
      <Box className='claims' display='flex' flexDirection='column'>
        <Box
          mb={1}
          px={3}
          py={2}
          display='flex'
          justifyContent='flex-end'
          alignItems='center'
          gap={1}>
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              startIcon={<HistoryIcon />}
              onClick={handleDisplaySyncRequests}>
              See sync requests
            </Button>
            <Button
              variant='contained'
              startIcon={<SyncIcon />}
              onClick={() => setSyncDrawerOpen(true)}
              sx={{
                backgroundColor: StyledTheme.tealDark,
                '&:hover': { backgroundColor: StyledTheme.tealDark },
              }}>
              Sync claims
            </Button>
          </Stack>
        </Box>
        <Box>
          <CustomTabs
            value={viewIndex >= 0 ? viewIndex : 0}
            onChange={handleViewChange}
            aria-label='Claims view'
            tabLabels={[
              { label: 'Calendar', icon: <CalendarMonthIcon /> },
              { label: 'List', icon: <ListIcon /> },
            ]}
          />
        </Box>
        <TabPanel value={viewIndex} index={1} sx={{ p: 0 }}>
          <ClaimsList
            clinicOptions={clinicOptions}
            onClaimClick={(claim) => openClaimInUrl(claim.id)}
            onMessage={setMessage}
          />
        </TabPanel>
        <TabPanel value={viewIndex} index={0} sx={{ p: 0 }}>
        <Box mb={2}>
          <Filters
            claimType={claimType}
            clinic={clinics}
            clinics={clinicOptions}
            filterLoading={filterLoading}
            onClaimStatusChange={handleStatusChange}
            onClaimTypeChange={handleClaimTypeChange}
            onClinicChange={handleClinicChange}
            onFilter={handleFilter}
            onPatientChange={handlePatientChange}
            patient={patient}
            patientOptions={patientOptionsFromClaims}
            status={status}
          />
        </Box>
        <Box p={1}>
          <ClaimLegend />
          <ClaimsCalendar
            onEventClick={handleEventClick}
            onDateSetClick={handleDateSetClick}
            claims={calendarFilteredClaims}
            isLoading={calendarLoading}
            pageProgress={pageProgress}
            onCancel={handleCancelFetch}
          />
        </Box>
        </TabPanel>
      </Box>
      {viewingClaimRequests && (
        <Box className='claim-requests side-panel'>
          <ClaimSyncRequests onClose={handleCloseClaimRequests} />
        </Box>
      )}
      <AppDrawer
        open={syncDrawerOpen}
        onClose={() => setSyncDrawerOpen(false)}
        title='Sync claims'>
        <SyncClaimsDrawer
          clinic={clinics}
          clinics={clinicOptions}
          fromSyncDate={fromSyncDate}
          untilSyncDate={untilSyncDate}
          forceEhrFetch={forceEhrFetching}
          syncLoading={syncLoading}
          lastSyncedAt={lastSyncedAt}
          onClinicChange={handleClinicChange}
          onFromSyncDateChange={handleFromSyncDateChange}
          onUntilSyncDateChange={handleUntilSyncDateChange}
          onForceEhrFetch={handleForceEhrFetch}
          onConfirmSync={handleSyncRange}
        />
      </AppDrawer>
      <AppDrawer
        open={claimIdInUrl != null}
        onClose={handleCloseDetails}
        title={
          selectedClaim
            ? `Claim #${selectedClaim.id}`
            : claimIdInUrl != null
            ? `Claim #${claimIdInUrl}`
            : 'Claim'
        }>
        {selectedClaimLoading && !selectedClaim ? (
          <Loader />
        ) : selectedClaim ? (
          <ClaimDetails
            clinics={clinicOptions}
            forceEhrFetch={forceResyncEhrFetching}
            onForceEhrFetch={handleForceResyncEhrFetch}
            claim={selectedClaim}
            onClose={handleCloseDetails}
            onResync={handleAttemptResync}
            onClaimUpdate={handleClaimUpdate}
            onClaimDeleted={async (deletedId) => {
              setClaims((prev) => prev.filter((c) => c.id !== deletedId));
              if (fromFilterDate && untilFilterDate) {
                await handleFetchClaims(fromFilterDate, untilFilterDate);
              }
            }}
          />
        ) : null}
      </AppDrawer>
      <Portal>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={!!message}
          autoHideDuration={10000}
          onClose={() => setMessage('')}
          message={message}
        />
      </Portal>
    </>
  );
};
